
import React, { useState, useEffect } from 'react'
import { StripeProvider, Elements } from 'react-stripe-elements'
import { Switch, Route, Redirect } from 'react-router-dom'
import AccountSettingsForm from './AccountSettingsForm'
import config from '@apex/lib/config'
import Subscriptions from './Subscriptions'
import ConfirmationDialog from './ConfirmationDialog'
import CouponForm from './CouponForm'
import TeamMembers from './TeamMembers'
import BillingForm from './BillingForm'
import Placeholder from './Placeholder'
import CardForm from './CardForm'
import Invoices from './Invoices'
import Dialog from './Dialog'
import toaster from './Toasts'
import Loader from './Loader'
import Teams from './Teams'
import Page from './Page'
import { HBox } from './Box'

/**
 * TeamRoutes component.
 */

export default function TeamRoutes({ root, ...props }) {
  return <Switch>
    {root && <Redirect exact from="/" to="/teams" />}
    <Redirect exact from="/team" to="/team/settings" />
    <Route exact path="/team/payment" render={_ => <TeamCardPage {...props} />} />
    <Route exact path="/team/settings" render={_ => <TeamSettingsPage {...props} />} />
    <Route exact path="/team/members" render={_ => <TeamMembersPage {...props} />} />
    <Route exact path="/team/invoices" render={_ => <TeamInvoicesPage {...props} />} />
    <Route exact path="/team/subscriptions" render={_ => <TeamSubscriptionsPage {...props} />} />
    <Route exact path="/teams" render={_ => <TeamsPage {...props} />} />
    <Route exact path="/account/settings" render={_ => <AccountSettingsPage {...props} />} />
    <Route exact path="/logout" render={_ => <Logout {...props} />} />
  </Switch>
}

/**
 * Content wrapper.
 */

function Content({ children }) {
  return children
}

/**
 * AccountSettingsPage shows the user settings form.
 */

function AccountSettingsPage({ user, actions }) {
  async function submit(user) {
    // TODO: add trimming to form by default?
    user.name = user.name.trim()
    await actions.updateUser(user)
    toaster.success('Settings updated')
  }

  return <Content>
    <Page.Title text="Profile" />
    <Page.Content.Wrapper>
      <AccountSettingsForm {...user} onSubmit={submit} />
    </Page.Content.Wrapper>
  </Content>
}

/**
 * TeamsPage shows the teams the user owns.
 */

function TeamsPage({ user, teams, actions }) {
  const [confirming, setConfirming] = useState(null)
  const [removing, setRemoving] = useState(null)

  async function remove(team) {
    setRemoving(team)
    await actions.removeTeam({ team_id: team.id })
    actions.initializeActiveTeam()
    setRemoving(null)
  }

  const dialog = confirming && <ConfirmationDialog
    text={`Are you are you want to remove "${confirming.name}"?`}
    onCloseComplete={_ => setConfirming(null)}
    onConfirm={_ => remove(confirming)}
    confirmLabel="Remove"
    theme="danger"
  />

  return <Content>
    {dialog}
    <Page.Title text="Teams" />
    <Page.Content.Wrapper>
      <Teams
        user={user}
        removing={removing}
        teams={teams.filter(t => t.user_id == user.id)}
        onAdd={team => actions.addTeam(team)}
        onRemove={team => setConfirming(team)} />
    </Page.Content.Wrapper>
  </Content>
}

/**
 * TeamSubscriptionsPage shows the current subscriptions.
 */

function TeamSubscriptionsPage({ team, card, products, subscriptions, actions, onSubscribe, onUnsubscribe }) {
  const [pendingSubscription, setPendingSubscription] = useState(null)
  const [pendingPlanId, setPendingPlanId] = useState(null)
  const [applyingCoupon, setApplyingCoupon] = useState(null)

  useEffect(_ => {
    actions.loadSubscriptions({ team_id: team.id })
    actions.loadCard({ team_id: team.id })
  }, [team])

  useEffect(_ => {
    actions.loadProducts()
  }, [])

  if (card == null || products == null || subscriptions == null) {
    return <React.Fragment>
      <Page.Title text="Subscriptions" />
      <Loader />
    </React.Fragment>
  }

  // cancel
  async function cancel(e) {
    setPendingPlanId(e.plan_id)
    await actions.removeSubscription(e)
    setPendingPlanId(null)
    if (onUnsubscribe) onUnsubscribe()
  }

  // add or update
  async function subscribe(e) {
    setPendingPlanId(e.plan_id)
    
    // no card
    if (card.data == null) {
      setPendingSubscription(e)
      return
    }
    
    // update
    if (e.subscription_id) {
      await actions.updateSubscription(e)
      setPendingPlanId(null)
      return
    }
    
    // add
    await actions.addSubscription(e)
    toaster.success('Thanks for subscribing!')
    setPendingPlanId(null)
    
    if (onSubscribe) onSubscribe()
  }

  // add credit card
  async function addCard({ address, token }, sub) {
    await actions.addCard({ team, token })
    await actions.addSubscription(sub)
    
    if (onSubscribe) onSubscribe()

    // only derive the address from the card when missing.
    if (!team.address_country) {
      // here we have to be careful as `address` contains a
      // name field for the credit card holder.
      const { name, ...addressFields } = address

      await actions.updateTeam({
        team: {
          ...team,
          ...addressFields
        }
      })
    }

    setPendingPlanId(null)
    toaster.success('Thanks for subscribing!')
  }

  // apply a discount coupon
  function onApplyCoupon(close) {
    return async function({ op, coupon }) {
      // cancel
      if (op == 'cancel') {
        close()
        return
      }

      // validate
      const { valid, name } = await actions.validateCoupon({ coupon })
      if (!valid) {
        toaster.danger('Invalid coupon')
        return
      }

      // apply coupon to subscription
      const sub = { coupon, ...applyingCoupon }
      await actions.updateSubscription(sub)

      // reload
      actions.loadSubscriptions({ team_id: team.id })

      toaster.success(`Coupon applied: ${name}`)
      setApplyingCoupon(null)
      close()
    }
  }

  const cardDialog = pendingSubscription && <Dialog onClose={_ => setPendingSubscription(null)}>
    {({ close }) => {
      return <StripeCardForm dialog buttonText="Add card" onSubmit={e => (close(), addCard(e, pendingSubscription))} />
    }}
  </Dialog>

  const couponDialog = applyingCoupon && <Dialog onClose={_ => setApplyingCoupon(null)}>
    {({ close }) => {
      return <CouponForm onSubmit={onApplyCoupon(close)} />
    }}
  </Dialog>

  return <Content>
    {cardDialog}
    {couponDialog}
    <Page.Title text="Subscriptions" />
    <Page.Content.Wrapper>
      <Subscriptions
        team={team}
        card={card}
        products={products}
        pendingPlanId={pendingPlanId}
        subscriptions={subscriptions.filter(s => s.status == 'active' || s.status == 'trialing')}
        onApplyCoupon={e => setApplyingCoupon(e)}
        onSubscribe={subscribe}
        onCancel={cancel} />
    </Page.Content.Wrapper>
  </Content>
}

/**
 * TeamInvoicesPage shows the subscription invoices.
 */

function TeamInvoicesPage({ team, products, subscriptions, invoices, actions }) {
  useEffect(_ => {
    actions.loadSubscriptions({ team_id: team.id })
    actions.loadInvoices({ team_id: team.id })
    actions.loadProducts()
  }, [team])

  if (products == null || subscriptions == null || invoices == null) {
    return <React.Fragment>
      <Page.Title text="Invoices" />
      <Loader />
    </React.Fragment>
  }

  if (subscriptions.length == 0) {
    return <Content>
      <Page.Title text="Invoices" />
      <Placeholder title="No invoices" text="Looks like you don't have any invoices." />
    </Content>
  }

  return <Content>
    <Page.Title text="Invoices" />
    <Page.Content.Wrapper>
      <Invoices
        team={team}
        products={products}
        subscriptions={subscriptions}
        invoices={invoices}
        actions={actions} />
    </Page.Content.Wrapper>
  </Content>
}

/**
 * TeamMembersPage shows the team members and pending invites.
 */

function TeamMembersPage({ team, members, invites, applicationName, applicationURL, actions }) {
  useEffect(_ => {
    actions.loadTeamMembers({ team_id: team.id })
    actions.loadTeamInvites({ team_id: team.id })
  }, [team])

  if (members == null || invites == null) {
    return <React.Fragment>
      <Page.Title text="Team members" />
      <Loader />
    </React.Fragment>
  }

  return <Content>
    <Page.Title text="Team members" />
    <Page.Content.Wrapper>
      <TeamMembers
        team={team}
        members={members}
        invites={invites}
        applicationName={applicationName}
        applicationURL={applicationURL}
        onAdd={member => actions.addTeamMember(member)}
        onRemove={member => actions.removeTeamMember(member)}
        flex />
    </Page.Content.Wrapper>
  </Content>
}

/**
 * TeamSettingsPage shows the team settings form.
 */

function TeamSettingsPage({ team, actions }) {
  async function submit(values) {
    team = { ...team, ...values }
    await actions.updateTeam({ team })
    toaster.success('Settings updated')
  }

  return <Content>
    <Page.Title text="Team settings" />
    <Page.Content.Wrapper>
      <BillingForm
        key={team.id}
        team={team}
        onSubmit={submit} />
    </Page.Content.Wrapper>
  </Content>
}

/**
 * TeamCardPage shows the stripe card form.
 */

function TeamCardPage({ team, card, actions }) {
  useEffect(_ => {
    actions.loadCard({ team_id: team.id })
  }, [team])

  if (card == null) {
    return <React.Fragment>
      <Page.Title text="Payment" />
      <Loader />
    </React.Fragment>
  }

  async function submit({ address, token }) {
    await actions.addCard({ team, token })
    
    // only derive the address from the card when missing
    if (!team.address_country) {
      await actions.updateTeam({
        team: {
          ...team,
          ...address
        }
      })
    }

    toaster.success('Updated payment information')
  }

  return <Content>
    <Page.Title text="Payment" />
    <Page.Content.Wrapper>
      <StripeCardForm {...card.data} onSubmit={submit} />
    </Page.Content.Wrapper>
  </Content>
}

/**
 * Logout signs the user out and redirects to /.
 */

function Logout({ actions }) {
  useEffect(_ => actions.logout())

  return <div>
    <Redirect to="/" />
  </div>
}

/**
 * StripeCardForm is a component which wraps CardForm in a StripeProvider.
 */

function StripeCardForm({ ...props }) {
  return <StripeProvider apiKey={config.stripe.key}>
    <Elements>
      <CardForm {...props} />
    </Elements>
  </StripeProvider>
}

